import { Point, HoveredHandle } from './types'
import { MapElement, MapSettings } from '../../../types'
import { getElementBounds, isLineBasedElement } from './geometryUtils'

const drawGrid = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  mapSettings: MapSettings,
  panOffset: Point,
  zoom: number,
) => {
  if (!mapSettings.showGrid) return

  const gridSize = mapSettings.gridSize

  // Calculate extended grid based on pan offset to create infinite appearance
  const startX = Math.floor((-panOffset.x * zoom) / gridSize) * gridSize
  const startY = Math.floor((-panOffset.y * zoom) / gridSize) * gridSize
  const endX = startX + width + gridSize
  const endY = startY + height + gridSize

  ctx.strokeStyle = '#f0f0f0'
  ctx.lineWidth = 1

  // Vertical lines - infinite grid
  for (let x = startX; x <= endX; x += gridSize) {
    ctx.beginPath()
    ctx.moveTo(x, startY)
    ctx.lineTo(x, endY)
    ctx.stroke()
  }

  // Horizontal lines - infinite grid
  for (let y = startY; y <= endY; y += gridSize) {
    ctx.beginPath()
    ctx.moveTo(startX, y)
    ctx.lineTo(endX, y)
    ctx.stroke()
  }
}

const drawBackgroundImage = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  mapSettings: MapSettings,
) => {
  if (!mapSettings.backgroundImage) return

  const img = new Image()
  img.onload = () => {
    ctx.save()

    const opacity = mapSettings.backgroundOpacity ?? 0.5
    const position = mapSettings.backgroundPosition ?? { x: 0, y: 0 }
    const size = mapSettings.backgroundSize ?? { width: 800, height: 600 }

    ctx.globalAlpha = opacity
    ctx.drawImage(img, position.x, position.y, size.width, size.height)
    ctx.restore()
  }
  img.src = mapSettings.backgroundImage
}

const drawHandles = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isHovered: boolean,
  hoveredHandle: HoveredHandle,
  hoveredVertexIndex: number,
) => {
  if (!isLineBasedElement(element)) {
    return
  }

  // Only show handles when selected or hovered
  if (!isSelected && !isHovered) {
    return
  }

  ctx.save()
  const HANDLE_SIZE = 6

  // Handle styling
  ctx.fillStyle = isSelected ? '#0969da' : '#666'
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 2

  // Handle polygons and fixture-polygons
  if (
    (element.type === 'polygon' || element.type === 'fixture-polygon') &&
    element.properties.vertices
  ) {
    const vertices = element.properties.vertices as Point[]
    vertices.forEach((vertex, index) => {
      ctx.beginPath()
      ctx.arc(vertex.x, vertex.y, HANDLE_SIZE, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()

      // Highlight hovered vertex handle
      if (
        isHovered &&
        hoveredHandle === 'vertex' &&
        hoveredVertexIndex === index
      ) {
        ctx.fillStyle = '#ff6b6b'
        ctx.beginPath()
        ctx.arc(vertex.x, vertex.y, HANDLE_SIZE + 2, 0, 2 * Math.PI)
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = isSelected ? '#0969da' : '#666'
      }
    })

    // Draw Bézier curve handles for polygon edges with curves
    if (element.curvedEdges && element.curvedEdges.length > 0) {
      drawCurveHandles(
        ctx,
        element,
        isSelected,
        isHovered,
        hoveredHandle,
        HANDLE_SIZE,
      )
    }
  } else if (
    (element.type === 'room' ||
      element.type === 'rectangle' ||
      element.type === 'stairs' ||
      element.type === 'fixture-rect') &&
    element.properties.width &&
    element.properties.height
  ) {
    // Handle room/rectangle/stairs corner handles
    const corners = [
      { x: element.position.x, y: element.position.y }, // top-left
      {
        x: element.position.x + element.properties.width,
        y: element.position.y,
      }, // top-right
      {
        x: element.position.x + element.properties.width,
        y: element.position.y + element.properties.height,
      }, // bottom-right
      {
        x: element.position.x,
        y: element.position.y + element.properties.height,
      }, // bottom-left
    ]

    corners.forEach((corner, index) => {
      ctx.beginPath()
      ctx.arc(corner.x, corner.y, HANDLE_SIZE, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()

      // Highlight hovered corner handle
      if (
        isHovered &&
        hoveredHandle === 'vertex' &&
        hoveredVertexIndex === index
      ) {
        ctx.fillStyle = '#ff6b6b'
        ctx.beginPath()
        ctx.arc(corner.x, corner.y, HANDLE_SIZE + 2, 0, 2 * Math.PI)
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = isSelected ? '#0969da' : '#666'
      }
    })
  } else if (
    (element.type === 'circle' || element.type === 'fixture-circle') &&
    element.properties.radius
  ) {
    // Handle circle and fixture-circle edge handles
    const radius = element.properties.radius
    const center = {
      x: element.position.x + radius,
      y: element.position.y + radius,
    }
    const handles = [
      { x: center.x, y: center.y - radius }, // top
      { x: center.x + radius, y: center.y }, // right
      { x: center.x, y: center.y + radius }, // bottom
      { x: center.x - radius, y: center.y }, // left
    ]

    handles.forEach((handle, index) => {
      ctx.beginPath()
      ctx.arc(handle.x, handle.y, HANDLE_SIZE, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()

      // Highlight hovered edge handle
      if (
        isHovered &&
        hoveredHandle === 'vertex' &&
        hoveredVertexIndex === index
      ) {
        ctx.fillStyle = '#ff6b6b'
        ctx.beginPath()
        ctx.arc(handle.x, handle.y, HANDLE_SIZE + 2, 0, 2 * Math.PI)
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = isSelected ? '#0969da' : '#666'
      }
    })
  } else if (element.properties.endPosition) {
    // Handle line-based elements (wall, door, window, line)

    // Draw start handle
    ctx.beginPath()
    ctx.arc(element.position.x, element.position.y, HANDLE_SIZE, 0, 2 * Math.PI)
    ctx.fill()
    ctx.stroke()

    // Draw end handle
    ctx.beginPath()
    ctx.arc(
      element.properties.endPosition.x,
      element.properties.endPosition.y,
      HANDLE_SIZE,
      0,
      2 * Math.PI,
    )
    ctx.fill()
    ctx.stroke()

    // Highlight hovered handle
    if (isHovered && hoveredHandle) {
      ctx.fillStyle = '#ff6b6b'
      const handlePos =
        hoveredHandle === 'start'
          ? element.position
          : element.properties.endPosition
      ctx.beginPath()
      ctx.arc(handlePos.x, handlePos.y, HANDLE_SIZE + 2, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()
    }
  }

  ctx.restore()
}

const drawCurveHandles = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isHovered: boolean,
  hoveredHandle: HoveredHandle,
  handleSize: number,
) => {
  if (!element.curvedEdges || !element.properties.vertices) return

  const vertices = element.properties.vertices as Point[]
  const curvedEdges = element.curvedEdges

  curvedEdges.forEach(curvedEdge => {
    const startVertex = vertices[curvedEdge.edgeIndex]
    const endVertex = vertices[(curvedEdge.edgeIndex + 1) % vertices.length]

    if (!startVertex || !endVertex) return

    // Get control points
    let cp1 = curvedEdge.controlPoint1
    let cp2 = curvedEdge.controlPoint2

    // If control points are not set, calculate defaults
    if (
      !cp1 ||
      !cp2 ||
      (cp1.x === 0 && cp1.y === 0) ||
      (cp2.x === 0 && cp2.y === 0)
    ) {
      const dx = endVertex.x - startVertex.x
      const dy = endVertex.y - startVertex.y
      const perpX = -dy
      const perpY = dx
      const length = Math.sqrt(perpX * perpX + perpY * perpY)
      const normalizedPerpX = perpX / length
      const normalizedPerpY = perpY / length
      const curveDistance =
        ((curvedEdge.curvature ?? 50) / 100) * (length * 0.5)
      const offsetX = normalizedPerpX * curveDistance
      const offsetY = normalizedPerpY * curveDistance

      cp1 = {
        x: startVertex.x + dx * 0.33 + offsetX,
        y: startVertex.y + dy * 0.33 + offsetY,
      }
      cp2 = {
        x: startVertex.x + dx * 0.66 + offsetX,
        y: startVertex.y + dy * 0.66 + offsetY,
      }
    }

    // Draw control lines (dashed lines from endpoints to control points)
    ctx.save()
    ctx.setLineDash([3, 3])
    ctx.strokeStyle = '#999'
    ctx.lineWidth = 1

    // Line from start vertex to first control point
    ctx.beginPath()
    ctx.moveTo(startVertex.x, startVertex.y)
    ctx.lineTo(cp1.x, cp1.y)
    ctx.stroke()

    // Line from end vertex to second control point
    ctx.beginPath()
    ctx.moveTo(endVertex.x, endVertex.y)
    ctx.lineTo(cp2.x, cp2.y)
    ctx.stroke()

    ctx.restore()

    // Draw control point handles - make them larger and easier to grab
    ctx.save()
    ctx.fillStyle = '#ff9500' // Orange for curve handles
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2

    const controlHandleSize = 12 // Increased from 6 to 12 for better grabbing

    // Control point 1
    ctx.beginPath()
    ctx.rect(
      cp1.x - controlHandleSize / 2,
      cp1.y - controlHandleSize / 2,
      controlHandleSize,
      controlHandleSize,
    )
    ctx.fill()
    ctx.stroke()

    // Highlight if hovered
    if (hoveredHandle === 'controlPoint1') {
      ctx.fillStyle = '#ff6600'
      ctx.beginPath()
      ctx.rect(
        cp1.x - (controlHandleSize + 3) / 2,
        cp1.y - (controlHandleSize + 3) / 2,
        controlHandleSize + 3,
        controlHandleSize + 3,
      )
      ctx.fill()
      ctx.stroke()
    }

    // Control point 2
    ctx.fillStyle = '#ff9500' // Reset color
    ctx.beginPath()
    ctx.rect(
      cp2.x - controlHandleSize / 2,
      cp2.y - controlHandleSize / 2,
      controlHandleSize,
      controlHandleSize,
    )
    ctx.fill()
    ctx.stroke()

    // Highlight if hovered
    if (hoveredHandle === 'controlPoint2') {
      ctx.fillStyle = '#ff6600'
      ctx.beginPath()
      ctx.rect(
        cp2.x - (controlHandleSize + 3) / 2,
        cp2.y - (controlHandleSize + 3) / 2,
        controlHandleSize + 3,
        controlHandleSize + 3,
      )
      ctx.fill()
      ctx.stroke()
    }

    ctx.restore()
  })
}

const drawSelectionOutline = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  ctx.save()
  ctx.strokeStyle = '#0969da'
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])
  ctx.globalAlpha = 0.8

  // Draw selection box around element bounds
  const bounds = getElementBounds(element)
  if (bounds) {
    ctx.strokeRect(
      bounds.x - 5,
      bounds.y - 5,
      bounds.width + 10,
      bounds.height + 10,
    )
  }
  ctx.restore()
}

const drawLabelHandle = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isHovered: boolean,
) => {
  if (!element.properties.roomName && !element.properties.label) return

  // Calculate label position
  let defaultX = 0,
    defaultY = 0

  switch (element.type) {
    case 'room':
    case 'rectangle':
      if (element.properties.width && element.properties.height) {
        defaultX = element.position.x + element.properties.width / 2
        defaultY = element.position.y + element.properties.height / 2
      }
      break
    case 'circle':
      if (element.properties.radius) {
        defaultX = element.position.x + element.properties.radius
        defaultY = element.position.y + element.properties.radius
      }
      break
    case 'polygon':
      if (element.properties.vertices) {
        const vertices = element.properties.vertices as Point[]
        vertices.forEach(vertex => {
          defaultX += vertex.x
          defaultY += vertex.y
        })
        defaultX /= vertices.length
        defaultY /= vertices.length
      }
      break
    default:
      return
  }

  const labelOffsetX = element.labelPosition?.x || 0
  const labelOffsetY = element.labelPosition?.y || 0
  const labelX = defaultX + labelOffsetX
  const labelY = defaultY + labelOffsetY

  // Draw label handle when hovered
  if (isHovered) {
    ctx.save()
    ctx.fillStyle = '#0969da'
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2

    // Draw small handle near the label
    ctx.beginPath()
    ctx.arc(labelX + 20, labelY - 20, 4, 0, 2 * Math.PI)
    ctx.fill()
    ctx.stroke()

    // Draw connection line to label
    ctx.strokeStyle = '#0969da'
    ctx.lineWidth = 1
    ctx.setLineDash([2, 2])
    ctx.beginPath()
    ctx.moveTo(labelX + 20, labelY - 20)
    ctx.lineTo(labelX, labelY)
    ctx.stroke()
    ctx.setLineDash([])

    ctx.restore()
  }
}

export {
  drawGrid,
  drawBackgroundImage,
  drawHandles,
  drawSelectionOutline,
  drawLabelHandle,
}
